import {
  DevelopmentClient,
  IDevelopmentClient,
} from '@app/models/DevelopmentClient'
import { DevelopmentSite, IDevelopmentSite } from '@app/models/DevelopmentSite'
import { ISiteSuggestion, SiteSuggestion } from '@app/models/SiteSuggestion'
import { ApplicationState } from '@app/store'
import { errorToast, successToast } from '@app/utils/notifications'
import { IRequestOptions, makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface ISiteListState {
  readonly city: string
  readonly client: IDevelopmentClient
  readonly clientSlug: string
  readonly error?: string
  readonly filterText: string
  readonly isLoading: boolean
  readonly items: List<IDevelopmentSite>
  readonly sortOrder: string
  readonly status: string
  readonly suggestions: List<ISiteSuggestion>
  readonly totalItems: number
}

const SiteListStateRecord = Record({
  city: '',
  client: undefined,
  clientSlug: '',
  error: undefined,
  filterText: '',
  isLoading: false,
  items: List<DevelopmentSite>(),
  sortOrder: 'Status',
  status: '',
  suggestions: List<SiteSuggestion>(),
  totalItems: 0,
})

export class SiteListState extends SiteListStateRecord
  implements ISiteListState {
  public static fromJS(initialState?: Partial<ISiteListState>) {
    if (!initialState) {
      return new SiteListState()
    }

    const { client, items, suggestions, ...rest } = initialState
    return new SiteListState({
      client: DevelopmentClient.fromJS(client),
      items: items
        ? List(items.map((i) => DevelopmentSite.fromJS(i)))
        : undefined,
      suggestions: suggestions
        ? List(suggestions.map((i) => SiteSuggestion.fromJS(i)))
        : List<SiteSuggestion>(),
      ...rest,
    })
  }

  public readonly city: string
  public readonly client: DevelopmentClient
  public readonly clientSlug: string
  public readonly error?: string
  public readonly filterText: string
  public readonly isLoading: boolean
  public readonly items: List<DevelopmentSite>
  public readonly sortOrder: string
  public readonly status: string
  public readonly suggestions: List<SiteSuggestion>
  public readonly totalItems: number

  constructor(props?: Partial<SiteListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  ApproveSuggestion = 'SITE_LIST_APPROVE_SUGGESTION',
  ChangeSuggestionStatus = 'SITE_LIST:CHANGE_SUGGESTION_STATUS',
  ChangeSuggestionStatusFailure = 'SITE_LIST:CHANGE_SUGGESTION_STATUS_FAILURE',
  DenySuggestion = 'SITE_LIST::DENY_SUGGESTION',
  LoadClient = 'SITE_LIST::LOAD_CLIENT',
  LoadClientFailure = 'SITE_LIST::LOAD_CLIENT_FAILURE',
  LoadClientSuccess = 'SITE_LIST::LOAD_CLIENT_SUCCESS',
  LoadSites = 'SITE_LIST::LOAD_SITES',
  LoadSitesFailure = 'SITE_LIST::LOAD_SITES_FAILURE',
  LoadSitesRequest = 'SITE_LIST::LOAD_SITES_REQUEST',
  LoadSitesSuccess = 'SITE_LIST::LOAD_SITES_SUCCESS',
  LoadSuggestions = 'SITE_LIST::LOAD_SUGGESTIONS',
  LoadSuggestionsFailure = 'SITE:LIST::LOAD_SUGGESTIONS_FAILURE',
  LoadSuggestionsSuccess = 'SITE_LIST::LOAD_SUGGESTIONS_SUCCESS',
  SetCity = 'SITE_LIST::SET_CITY',
  SetFilterText = 'SITE_LIST::SET_FILTER_TEXT',
  SetSort = 'SITE_LIST::SET_SORT',
  SetStatus = 'SITE_LIST::SET_STATUS',
}

interface IApproveSuggestionAction {
  suggestionId: string
  type: ActionTypes.ApproveSuggestion
}

interface IChangeSuggestionStatusAction {
  status: string
  suggestionId: string
  type: ActionTypes.ChangeSuggestionStatus
}

interface IChangeSuggestionStatusFailureAction {
  type: ActionTypes.ChangeSuggestionStatusFailure
}

interface IDenySuggestionAction {
  suggestionId: string
  type: ActionTypes.DenySuggestion
}

interface ILoadClientAction {
  clientSlug: string
  type: ActionTypes.LoadClient
}

interface ILoadClientFailureAction {
  type: ActionTypes.LoadClientFailure
}

interface ILoadClientSuccessAction {
  client: DevelopmentClient
  type: ActionTypes.LoadClientSuccess
}

interface ILoadSitesAction {
  append: boolean
  clientSlug?: string
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: ActionTypes.LoadSites
}

interface ILoadSitesFailureAction {
  error: string
  type: ActionTypes.LoadSitesFailure
}

interface ILoadSitesRequestAction {
  type: ActionTypes.LoadSitesRequest
}

interface ILoadSitesSuccessAction {
  append: boolean
  items: DevelopmentSite[]
  totalItems: number
  type: ActionTypes.LoadSitesSuccess
}

interface ILoadSuggestionsAction {
  type: ActionTypes.LoadSuggestions
}

interface ILoadSuggestionsFailureAction {
  type: ActionTypes.LoadSuggestionsFailure
}

interface ILoadSuggestionsSuccessAction {
  suggestions: SiteSuggestion[]
  type: ActionTypes.LoadSuggestionsSuccess
}

interface ISetCityAction {
  type: ActionTypes.SetCity
  value: string
}

interface ISetFilterTextAction {
  filter: string
  type: ActionTypes.SetFilterText
}

interface ISetSortAction {
  type: ActionTypes.SetSort
  value: string
}

interface ISetStatusAction {
  type: ActionTypes.SetStatus
  value: string
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IChangeSuggestionStatusAction
  | IChangeSuggestionStatusFailureAction
  | ILoadClientFailureAction
  | ILoadClientSuccessAction
  | ILoadSitesFailureAction
  | ILoadSitesRequestAction
  | ILoadSitesSuccessAction
  | ILoadSuggestionsFailureAction
  | ILoadSuggestionsSuccessAction
  | ISetCityAction
  | ISetFilterTextAction
  | ISetSortAction
  | ISetStatusAction

// -------------------------
// ACTION CREATORS
// -------------------------
function approveSuggestion(suggestionId: string) {
  return {
    suggestionId,
    type: ActionTypes.ApproveSuggestion,
  }
}

function changeSuggestionStatus(suggestionId: string, status: string) {
  return { status, suggestionId, type: ActionTypes.ChangeSuggestionStatus}
}

function changeSuggestionStatusFailure() {
  return { type: ActionTypes.ChangeSuggestionStatusFailure}
}

function denySuggestion(suggestionId: string) {
  return {
    suggestionId,
    type: ActionTypes.DenySuggestion,
  }
}

function loadClient(clientSlug?: string) {
  return {
    clientSlug,
    type: ActionTypes.LoadClient,
  }
}

function loadClientFailure() {
  return { type: ActionTypes.LoadClientFailure }
}

function loadClientSuccess(client: DevelopmentClient) {
  return {
    client,
    type: ActionTypes.LoadClientSuccess,
  }
}

function loadSites(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
  clientSlug?: string,
): ILoadSitesAction {
  return {
    append,
    clientSlug,
    loadComplete,
    startIndex,
    stopIndex,
    type: ActionTypes.LoadSites,
  }
}

function loadSitesFailure(error: string): ILoadSitesFailureAction {
  return {
    error,
    type: ActionTypes.LoadSitesFailure,
  }
}

function loadSitesRequest(): ILoadSitesRequestAction {
  return {
    type: ActionTypes.LoadSitesRequest,
  }
}

function loadSitesSuccess(
  items: DevelopmentSite[],
  append: boolean,
  totalItems: number,
): ILoadSitesSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: ActionTypes.LoadSitesSuccess,
  }
}

function loadSuggestions(): ILoadSuggestionsAction {
  return { type: ActionTypes.LoadSuggestions }
}

function loadSuggestionFailure(): ILoadSuggestionsFailureAction {
  return {
    type: ActionTypes.LoadSuggestionsFailure,
  }
}

function loadSuggestionSuccess(
  suggestions: SiteSuggestion[],
): ILoadSuggestionsSuccessAction {
  return {
    suggestions,
    type: ActionTypes.LoadSuggestionsSuccess,
  }
}

function setCity(value: string): ISetCityAction {
  return {
    type: ActionTypes.SetCity,
    value,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: ActionTypes.SetFilterText,
  }
}

function setSort(value: string): ISetSortAction {
  return {
    type: ActionTypes.SetSort,
    value,
  }
}

function setStatus(value: string): ISetStatusAction {
  return {
    type: ActionTypes.SetStatus,
    value,
  }
}

export const actionCreators = {
  approveSuggestion,
  denySuggestion,
  loadClient,
  loadSites,
  loadSuggestions,
  setCity,
  setFilterText,
  setSort,
  setStatus,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<SiteListState> = (
  state: SiteListState = new SiteListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.ChangeSuggestionStatusFailure:
      return state as SiteListState

    case ActionTypes.ChangeSuggestionStatus:
        const indexToUpdate = state
        .getIn(['suggestions'])
        .findIndex((item: any) => {
          return item.get('id') === action.suggestionId
        })

        return state.setIn(
          ['suggestions', indexToUpdate, 'status'],
          action.status) as SiteListState

    case ActionTypes.LoadClientFailure:
      return state as SiteListState

    case ActionTypes.LoadClientSuccess:
      return state
        .set('client', action.client)
        .set('clientSlug', action.client.slug) as SiteListState

    case ActionTypes.LoadSitesFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as SiteListState

    case ActionTypes.LoadSitesRequest:
      return state.set('isLoading', true) as SiteListState

    case ActionTypes.LoadSitesSuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<DevelopmentSite>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as SiteListState

    case ActionTypes.LoadSuggestionsFailure:
      return state as SiteListState

    case ActionTypes.LoadSuggestionsSuccess:
      return state.set(
        'suggestions',
        List(action.suggestions),
      ) as SiteListState

    case ActionTypes.SetCity:
      return state.set('city', action.value) as SiteListState

    case ActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as SiteListState

    case ActionTypes.SetSort:
      return state.set('sortOrder', action.value) as SiteListState

    case ActionTypes.SetStatus:
      return state.set('status', action.value) as SiteListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* approveSuggestionSaga(action: IApproveSuggestionAction) {
  try {
    const currentState: SiteListState = yield select(
      (state: ApplicationState) => state.siteList,
    )

    const suggestionId = action.suggestionId

    const reqOpts: IRequestOptions = {
      body: JSON.stringify({suggestionId}),
      method: RequestMethod.PUT,
    }

    const reqUrl = `/api/sites/${currentState.client.id}/suggestions/${suggestionId}/approve`

    const result = yield makeAuthenticatedRequest(reqUrl, reqOpts)

    if (result.success === true) {
      yield put(changeSuggestionStatus(suggestionId, 'Approved'))
      yield put(successToast('Success!', `Suggestion Approved`))
    } else {
      yield put(changeSuggestionStatusFailure())
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(changeSuggestionStatusFailure())
    yield put(errorToast('Oops!', err.message))
  }
}

function* denySuggestionSaga(action: IDenySuggestionAction) {
  try {
    const currentState: SiteListState = yield select(
      (state: ApplicationState) => state.siteList,
    )

    const suggestionId = action.suggestionId

    const reqOpts: IRequestOptions = {
      body: JSON.stringify({suggestionId}),
      method: RequestMethod.PUT,
    }

    const reqUrl = `/api/sites/${currentState.client.id}/suggestions/${suggestionId}/deny`

    const result = yield makeAuthenticatedRequest(reqUrl, reqOpts)

    if (result.success === true) {
      yield put(changeSuggestionStatus(suggestionId, 'Denied'))
      yield put(successToast('Success!', `Suggestion Denied`))
    } else {
      yield put(changeSuggestionStatusFailure())
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(changeSuggestionStatusFailure())
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadClientSaga(action: ILoadClientAction) {
  try {
    const baseUrl = `/api/DevelopmentClients/${action.clientSlug}`

    const result = yield makeAuthenticatedRequest(baseUrl)

    if (result.success === true) {
      const client: DevelopmentClient = DevelopmentClient.fromJS(result.entity)
      yield put(loadClientSuccess(client))
      yield put(loadSites(0, 20, false))
    } else {
      yield put(loadClientFailure())
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadClientFailure())
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadSitesSaga(action: ILoadSitesAction) {
  try {
    yield put(loadSitesRequest())

    const currentState: SiteListState = yield select(
      (state: ApplicationState) => state.siteList,
    )

    const query: string[] = []

    if (currentState.filterText) {
      query.push(`filter=${encodeURIComponent(currentState.filterText)}`)
    }

    if (currentState.city) {
      query.push(`city=${currentState.city}`)
    }

    if (currentState.sortOrder) {
      query.push(`sort=${currentState.sortOrder}`)
    }

    if (currentState.status) {
      query.push(`status=${currentState.status}`)
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    if (!currentState.client || !currentState.client.id) {
      return null
    }

    const baseUrl = `/api/developmentClients/${currentState.client.id}/sites`

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: DevelopmentSite[] = result.results.map((c: any) =>
        DevelopmentSite.fromJS(c),
      )
      yield put(loadSitesSuccess(contacts, action.append, result.paging.total))
    } else {
      yield put(loadSitesFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadSitesFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadSuggestionsSaga(action: ILoadSuggestionsAction) {
  try {
    const currentState: SiteListState = yield select(
      (state: ApplicationState) => state.siteList,
    )

    const query: string[] = []

    if (currentState.filterText) {
      query.push(`filter=${encodeURIComponent(currentState.filterText)}`)
    }

    if (!currentState.client || !currentState.client.id) {
      return null
    }

    const baseUrl = `/api/sites/${currentState.client.id}/suggestions`

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const list: SiteSuggestion[] = result.results.map((c: any) =>
        SiteSuggestion.fromJS(c),
      )
      yield put(loadSuggestionSuccess(list))
    } else {
      yield put(loadSuggestionFailure())
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadSuggestionFailure())
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchApproveSuggestion() {
  yield takeLatest(ActionTypes.ApproveSuggestion, approveSuggestionSaga)
}

function* watchDenySuggestion() {
  yield takeLatest(ActionTypes.DenySuggestion, denySuggestionSaga)
}

function* watchLoadClient() {
  yield takeLatest(ActionTypes.LoadClient, loadClientSaga)
}

function* watchLoadSites() {
  yield takeLatest(ActionTypes.LoadSites, loadSitesSaga)
}

function* watchLoadSuggestions() {
  yield takeLatest(ActionTypes.LoadSuggestions, loadSuggestionsSaga)
}

export function* siteListRootSaga() {
  yield fork(watchApproveSuggestion)
  yield fork(watchDenySuggestion)
  yield fork(watchLoadClient)
  yield fork(watchLoadSites)
  yield fork(watchLoadSuggestions)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): SiteListState =>
  state.siteList

export const selectCity = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.city,
  )

export const selectClient = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.client,
  )

export const selectClientId = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.clientSlug,
  )

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectFilterText = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.filterText,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.items,
  )

export const selectSort = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.sortOrder,
  )

export const selectStatus = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.status,
  )

export const selectSuggestions = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.suggestions,
  )

export const selectTotalItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.totalItems,
  )
